// pushNotification.jsx
import { useEffect } from "react";
import { Alert, Platform, Linking } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { initWebPushNotification } from "./webPushNotification";

let messaging;
if (Platform.OS !== "web") {
  messaging = require("@react-native-firebase/messaging").default;
}

const TOPIC_STORAGE_KEY = "subscribed_topics";
const NOTIFICATION_PERMISSION_ASKED = "notification_permission_asked";
const ANDROID_TIP_SHOWN = "android_optimization_tip_shown";

// ─── Native Permission Request ────────────────────────────────────────────────
const requestUserPermission = async () => {
  try {
    const authStatus = await messaging().requestPermission();
    return (
      authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
      authStatus === messaging.AuthorizationStatus.PROVISIONAL
    );
  } catch (err) {
    console.error("❌ requestPermission error:", err);
    return false;
  }
};

// ─── Custom Modal to Ask User (Native) ────────────────────────────────────────
const showPermissionModal = () => {
  return new Promise((resolve) => {
    Alert.alert(
      "🔔 Enable Notifications",
      "Stay updated with event announcements, schedule changes, and reminders.",
      [
        { text: "Not Now", style: "cancel", onPress: () => resolve(false) },
        { text: "Enable", onPress: () => resolve(true) },
      ],
      { cancelable: false }
    );
  });
};

// ─── Android Battery Optimization Tip ─────────────────────────────────────────
const showAndroidOptimizationModal = () => {
  return new Promise((resolve) => {
    Alert.alert(
      "📱 Enable Background Notifications",
      "To receive notifications when the app is closed or in background:\n\n" +
        "🔋 For better delivery:\n" +
        "• Go to App Settings\n" +
        "• Find 'Battery' or 'Power Management'\n" +
        "• Select 'Don't optimize' or 'Allow background activity'\n" +
        "• Enable 'Auto-start' if available\n\n" +
        "📲 This ensures you never miss important event updates!",
      [
        { text: "Maybe Later", style: "cancel", onPress: () => resolve(false) },
        {
          text: "Open Settings",
          onPress: () => {
            if (Platform.OS === "android") Linking.openSettings();
            resolve(true);
          },
        },
      ],
      { cancelable: false }
    );
  });
};

// ─── Subscribe / Unsubscribe Helpers (Native) ─────────────────────────────────
const subscribeToTopic = async (topicName) => {
  try {
    await messaging().subscribeToTopic(topicName);
    await AsyncStorage.setItem(TOPIC_STORAGE_KEY, JSON.stringify([topicName]));
    console.log("✅ Subscribed to topic:", topicName);
  } catch (err) {
    console.error("❌ Subscription failed:", err);
  }
};

const unsubscribeFromAllTopics = async () => {
  try {
    const data = await AsyncStorage.getItem(TOPIC_STORAGE_KEY);
    const topics = data ? JSON.parse(data) : [];
    for (const topic of topics) {
      await messaging().unsubscribeFromTopic(topic);
      console.log("❎ Unsubscribed from topic:", topic);
    }
    await AsyncStorage.removeItem(TOPIC_STORAGE_KEY);
  } catch (err) {
    console.error("❌ Unsubscription error:", err);
  }
};

// ─── Initialize Push (Web vs. Native) ─────────────────────────────────────────
export async function initPushNotification() {
  try {
    if (Platform.OS === "web") {
      // ➡️ Web-only initialization (alert + browser notification handled inside)
      return await initWebPushNotification();
    }

    // ─── Native (Android / iOS) Logic ─────────────────────────────────────────
    const hasAsked = await AsyncStorage.getItem(NOTIFICATION_PERMISSION_ASKED);
    if (!hasAsked) {
      const agreed = await showPermissionModal();
      if (!agreed) {
        await AsyncStorage.setItem(NOTIFICATION_PERMISSION_ASKED, "declined");
        return false;
      }
    }

    const granted = await requestUserPermission();
    if (!granted) {
      await AsyncStorage.setItem(NOTIFICATION_PERMISSION_ASKED, "denied");
      Alert.alert(
        "Notifications Disabled",
        "To re-enable: go to Settings → App → Notifications",
        [
          { text: "Cancel", style: "cancel" },
          { text: "Open Settings", onPress: () => Linking.openSettings() },
        ]
      );
      return false;
    }

    await AsyncStorage.setItem(NOTIFICATION_PERMISSION_ASKED, "granted");

    const token = await messaging().getToken();
    console.log("📲 FCM Token (native):", token);

    await unsubscribeFromAllTopics();
    const eventId = await AsyncStorage.getItem("eventId");
    if (eventId) await subscribeToTopic(eventId);

    // Show Android battery optimization tip if first time granted
    if (Platform.OS === "android" && hasAsked !== "granted") {
      setTimeout(() => showAndroidOptimizationModal(), 1500);
    }

    return true;
  } catch (err) {
    console.error("❌ initPushNotification error:", err);
    return false;
  }
}

// ─── Re-check Permissions at Any Time (Native) ────────────────────────────────
export async function checkNotificationStatus() {
  try {
    if (Platform.OS === "web") return true;

    const status = await messaging().hasPermission();
    const hasPerm =
      status === messaging.AuthorizationStatus.AUTHORIZED ||
      status === messaging.AuthorizationStatus.PROVISIONAL;

    if (!hasPerm) {
      const retry = await new Promise((resolve) => {
        Alert.alert(
          "🔔 Stay Updated!",
          "Enable notifications to receive event updates, announcements, and more.",
          [
            { text: "Not Now", style: "cancel", onPress: () => resolve(false) },
            { text: "Enable", onPress: () => resolve(true) },
          ],
          { cancelable: false }
        );
      });
      if (retry) return await initPushNotification();
      return false;
    }

    // If already granted, show Android optimization tip once
    const shown = await AsyncStorage.getItem(ANDROID_TIP_SHOWN);
    if (Platform.OS === "android" && !shown) {
      setTimeout(async () => {
        await showAndroidOptimizationModal();
        await AsyncStorage.setItem(ANDROID_TIP_SHOWN, "true");
      }, 1000);
    }

    return true;
  } catch (err) {
    console.error("❌ checkNotificationStatus error:", err);
    return false;
  }
}

// ─── React Hook: Mount & Register Handlers ────────────────────────────────────
export default function usePushNotification() {
  useEffect(() => {
    const setup = async () => {
      // 1️⃣ Initialize either web or native
      await initPushNotification();

      if (Platform.OS === "web") {
        // On web, the pop-up is handled inside initWebPushNotification() → onMessageListener()
        return;
      }

      // ─── Native-only: Foreground message handler ───────────────────────────────
      const unsubscribeForeground = messaging().onMessage((remoteMessage) => {
        console.log("📬 Foreground (native):", remoteMessage);
        const title = remoteMessage.notification?.title || "Notification";
        const body = remoteMessage.notification?.body || "";
        // Show React Native Alert on Android/iOS
        Alert.alert(title, body, [{ text: "OK" }], { cancelable: true });
      });

      // ─── Native-only: Background (notification opened) handler ─────────────────
      messaging().onNotificationOpenedApp((remoteMessage) => {
        console.log("📖 Opened from background (native):", remoteMessage.notification);
        if (remoteMessage.data?.screen) {
          console.log("🧭 Navigate to screen:", remoteMessage.data.screen);
        }
      });

      // ─── Native-only: Check if opened from killed state ────────────────────────
      messaging()
        .getInitialNotification()
        .then((remoteMessage) => {
          if (remoteMessage?.data?.screen) {
            console.log("🚀 Opened from quit (native):", remoteMessage.data.screen);
          }
        });

      return () => {
        unsubscribeForeground();
      };
    };

    setup();
  }, []);

  return null;
}
